import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PageFrame from "./PageFrame";
import DirectoryRowCard from "../components/DirectoryRowCard";
import {
  saasCircleCountSx,
  saasDirectoryWrapSx,
  saasHeroActionButtonSx,
  saasInputSx,
  saasSectionCardSx,
  saasSoftCountSx,
} from "../components/saasStyles";

const syncRows = [
  {
    id: "products-import",
    title: "Import produits depuis Odoo",
    subtitle: "SYNC-001",
    chips: [
      { label: "Import", color: "success" },
      { label: "Catalog", color: "default" },
    ],
    columns: [
      { label: "Direction", value: "Odoo -> Plateforme", secondary: "Produits, categories, prix" },
      { label: "Statut", value: "Pret", secondary: "Lancement manuel ou via job" },
    ],
  },
  {
    id: "orders-export",
    title: "Transfert des commandes vers Odoo",
    subtitle: "SYNC-002",
    chips: [
      { label: "Export", color: "success" },
      { label: "Commandes", color: "default" },
    ],
    columns: [
      { label: "Direction", value: "Plateforme -> Odoo", secondary: "Conversion prospect vers commande" },
      { label: "Statut", value: "Declenchement manuel", secondary: "Lance par le commercial" },
    ],
  },
  {
    id: "status-return",
    title: "Retour des statuts",
    subtitle: "SYNC-003",
    chips: [
      { label: "Inbound", color: "success" },
      { label: "Status", color: "default" },
    ],
    columns: [
      { label: "Direction", value: "Odoo -> Plateforme", secondary: "Pret a livrer, progression" },
      { label: "Statut", value: "Planifie", secondary: "Prochaine etape d'integration" },
    ],
  },
];

export default function OdooSyncPage() {
  const [search, setSearch] = useState("");
  const [quickAction, setQuickAction] = useState("default");
  const [selectedId, setSelectedId] = useState(null);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = !needle
      ? syncRows
      : syncRows.filter((row) =>
          [row.title, row.subtitle, ...row.columns.map((column) => `${column.value} ${column.secondary}`)]
            .join(" ")
            .toLowerCase()
            .includes(needle)
        );

    if (quickAction === "import") {
      return base.filter((row) => row.chips.some((chip) => chip.label === "Import"));
    }
    if (quickAction === "export") {
      return base.filter((row) => row.chips.some((chip) => chip.label === "Export"));
    }
    return base;
  }, [search, quickAction]);

  return (
    <PageFrame
      eyebrow="SmartDoctors"
    title="Synchronisation Odoo"
    description="Vue des imports, exports et actions d'integration Odoo."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<AddCircleRoundedIcon />} sx={saasHeroActionButtonSx}>
          + Nouvelle sync
        </Button>
      }
    >
      <Box sx={{ ...saasDirectoryWrapSx, borderRadius: "44px", px: { xs: 2, md: 2.5 }, py: { xs: 1.2, md: 1.4 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          Afficher {filteredRows.length} elements
        </Typography>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} alignItems={{ xs: "stretch", xl: "center" }}>
          <TextField
            label="Rechercher"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              ...saasInputSx,
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "18px",
                minHeight: 44,
                backgroundColor: "#fff",
                boxShadow: "none",
              },
            }}
            InputProps={{ startAdornment: <SearchRoundedIcon sx={{ color: "#5f6fff", mr: 1 }} /> }}
          />

          <FormControl sx={{ minWidth: { xs: "100%", xl: 350 } }}>
            <Select
              value={quickAction}
              onChange={(event) => setQuickAction(event.target.value)}
              displayEmpty
              sx={{ minHeight: 56, borderRadius: "18px", bgcolor: "#fff", fontWeight: 700, boxShadow: "none" }}
            >
              <MenuItem value="default">Action rapide</MenuItem>
              <MenuItem value="import">Imports</MenuItem>
              <MenuItem value="export">Exports</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ...saasSoftCountSx, minWidth: 104, minHeight: 44, bgcolor: "#f5f7ff" }}>
            <Typography variant="body2" sx={{ color: "#5867ff", fontWeight: 700 }}>
              {filteredRows.length} flux
            </Typography>
          </Box>

          <Box sx={{ width: 58, height: 44, borderRadius: "16px", border: "1px solid #dfe6f4", bgcolor: "#fff", display: "grid", placeItems: "center" }}>
            <Badge badgeContent={syncRows.length} color="error">
              <IconButton size="small" sx={{ color: "#5a68ff" }}>
                <NotificationsRoundedIcon />
              </IconButton>
            </Badge>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>
              Flux de synchronisation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vue structuree des correspondances, des sens de flux et de l'etat de l'integration.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25}>
            <Box sx={saasCircleCountSx}>
              <Box>
                <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                  Visibles
                </Typography>
                <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                  {filteredRows.length}
                </Typography>
              </Box>
            </Box>
            <Box sx={saasCircleCountSx}>
              <Box>
                <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                  Selection
                </Typography>
                <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                  {selectedId ? 1 : 0}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {filteredRows.map((row) => (
          <DirectoryRowCard
            key={row.id}
            selected={selectedId === row.id}
            avatarText="O"
            title={row.title}
            subtitle={row.subtitle}
            chips={row.chips}
            columns={row.columns}
            actionLabel={selectedId === row.id ? "Selectionne" : "Choisir"}
            onSelect={() => setSelectedId(row.id)}
          />
        ))}
      </Box>
    </PageFrame>
  );
}
